import { Box, styled } from '@mui/material';
import { FC } from 'react';
import { TextField, TextInput, TopToolbar } from 'react-admin';

import { BoListQueryBase } from '@Common/types/bo/bo_list_query';
import { GetCompanyResponse } from '@Common/types/bo_external/companies';

import { DatagridTyped, FilterButton, List } from '@sh4res/bo-common/components';
import { IdField, TextArrayInput, UTCDateField } from '@sh4res/bo-common/fields';
import { DateInput } from '@sh4res/bo-common/fields/DateInput';
import { useSelectedColumns } from '@sh4res/bo-common/hooks';
import { ColumnList, RaSort } from '@sh4res/bo-common/utils';
import { createFilters } from '@sh4res/bo-common/utils/create_filters';

type GetCompaniesQuery = BoListQueryBase;
type GetCompaniesOneResponse = Pick<
  GetCompanyResponse,
  | 'id'
  | 'name'
  | 'registration_date'
  | 'siret'
  | 'start_fiscal_year'
  | 'naf_code'
  | 'collective_agreement_code'
  | 'collective_agreement_name'
  | 'created_at'
  | 'updated_at'
>;

const b2bCompaniesFilters = createFilters<GetCompaniesQuery>({
  id: <TextArrayInput asNumber source="" data-testid={'BoExternalCompaniesList_IdFilter'} />,
  name: <TextArrayInput source="" data-testid={'BoExternalCompaniesList_NameFilter'} />,
  siret: <TextArrayInput source="" data-testid={'BoExternalCompaniesList_SiretFilter'} />,
  registration_date: (
    <DateInput source="" data-testid={'BoExternalCompaniesList_RegistrationDateFilter'} />
  ),
  start_fiscal_year: (
    <TextInput source="" data-testid={'BoExternalCompaniesList_StartFiscalYearFilter'} />
  ),
  naf_code: <TextArrayInput source="" data-testid={'BoExternalCompaniesList_NafCodeFilter'} />,
  collective_agreement_code: (
    <TextArrayInput
      source=""
      data-testid={'BoExternalCompaniesList_CollectiveAgreementCodeFilter'}
    />
  ),
  collective_agreement_name: (
    <TextArrayInput
      source=""
      data-testid={'BoExternalCompaniesList_CollectiveAgreementNameFilter'}
    />
  ),
  from_created_at: (
    <DateInput source="" data-testid={'BoExternalCompaniesList_FromCreatedAtFilter'} />
  ),
  to_created_at: <DateInput source="" data-testid={'BoExternalCompaniesList_ToCreatedAtFilter'} />,
});

const defaultSort: RaSort<GetCompaniesQuery> = { field: 'id', order: 'DESC' };

const companiesListColumns: ColumnList<GetCompaniesOneResponse> = {
  id: <IdField reference="companies" data-testid={'BoExternalCompaniesList_IdColumn'} />,
  name: <TextField data-testid={'BoExternalCompaniesList_NameColumn'} />,
  registration_date: (
    <UTCDateField data-testid={'BoExternalCompaniesList_RegistrationDateColumn'} />
  ),
  siret: <TextField data-testid={'BoExternalCompaniesList_SiretColumn'} />,
  start_fiscal_year: <TextField data-testid={'BoExternalCompaniesList_StartFiscalYearColumn'} />,
  naf_code: <TextField data-testid={'BoExternalCompaniesList_NafCodeColumn'} />,
  collective_agreement_code: (
    <TextField data-testid={'BoExternalCompaniesList_CollectiveAgreementCodeColumn'} />
  ),
  collective_agreement_name: (
    <TextField data-testid={'BoExternalCompaniesList_CollectiveAgreementNameColumn'} />
  ),
  created_at: <UTCDateField data-testid={'BoExternalCompaniesList_CreatedAtColumn'} />,
  updated_at: <UTCDateField data-testid={'BoExternalCompaniesList_UpdatedAtColumn'} />,
};

const defaultColumns: (keyof GetCompaniesOneResponse)[] = [
  'id',
  'name',
  'siret',
  'registration_date',
  'created_at',
  'updated_at',
];

const CompaniesActionsItem = styled(Box)`
  margin: ${({ theme }) => theme.spacing(0, 0, 0, 0.5)};
`;

const CompaniesActions = () => (
  <TopToolbar>
    <CompaniesActionsItem>
      <FilterButton data-testid={'BoExternalCompaniesActions_FilterButton'} />
    </CompaniesActionsItem>
  </TopToolbar>
);

// TODO: just a placeholder - https://sh4res.atlassian.net/browse/ES-263
export const CompaniesList: FC = () => {
  const displayedColumns = useSelectedColumns<GetCompaniesOneResponse>({
    preferences: 'companies.list.columns',
    defaultColumns,
  });

  return (
    <List
      actions={<CompaniesActions />}
      exporter={false}
      sort={defaultSort}
      filters={b2bCompaniesFilters}
      empty={false}>
      <DatagridTyped<GetCompaniesOneResponse>
        rowClick="show"
        bulkActionButtons={false}
        columnRegistry={companiesListColumns}
        displayedColumns={displayedColumns}
        sortableColumns={defaultColumns}
      />
    </List>
  );
};
